import api from '../utils/api';

/**
 * Ticket Service - Handles support ticket operations
 */

export const ticketService = {
  /**
   * Create a new support ticket
   * @param {Object} ticketData - Ticket data
   * @returns {Promise}
   */
  createTicket: async (ticketData) => {
    try {
      const response = await api.post('/tickets', ticketData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create ticket');
    }
  },

  /**
   * Get user's tickets
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getUserTickets: async (filters = {}) => {
    try {
      const response = await api.get('/tickets', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch tickets');
    }
  },

  /**
   * Get ticket by ID
   * @param {string} id - Ticket ID
   * @returns {Promise}
   */
  getTicketById: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch ticket');
    }
  },

  /**
   * Update ticket
   * @param {string} id - Ticket ID
   * @param {Object} updateData - Update data
   * @returns {Promise}
   */
  updateTicket: async (id, updateData) => {
    try {
      const response = await api.put(`/tickets/${id}`, updateData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update ticket');
    }
  },

  /**
   * Add comment to ticket
   * @param {string} id - Ticket ID
   * @param {Object} commentData - Comment data
   * @returns {Promise}
   */
  addComment: async (id, commentData) => {
    try {
      const response = await api.post(`/tickets/${id}/comments`, commentData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add comment');
    }
  },

  /**
   * Get ticket comments
   * @param {string} id - Ticket ID
   * @returns {Promise}
   */
  getComments: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}/comments`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch comments');
    }
  },

  /**
   * Get ticket history
   * @param {string} id - Ticket ID
   * @returns {Promise}
   */
  getTicketHistory: async (id) => {
    try {
      const response = await api.get(`/tickets/${id}/history`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch ticket history');
    }
  },

  /**
   * Submit customer satisfaction
   * @param {string} id - Ticket ID
   * @param {Object} satisfactionData - Satisfaction data
   * @returns {Promise}
   */
  submitSatisfaction: async (id, satisfactionData) => {
    try {
      const response = await api.post(`/tickets/${id}/satisfaction`, satisfactionData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to submit satisfaction');
    }
  },

  // Admin methods
  /**
   * Get all tickets (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getAllTickets: async (filters = {}) => {
    try {
      const response = await api.get('/admin/tickets', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch tickets');
    }
  },

  /**
   * Get ticket statistics (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  getTicketStats: async (filters = {}) => {
    try {
      const response = await api.get('/admin/tickets/stats', { params: filters });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch ticket stats');
    }
  },

  /**
   * Assign ticket to agent (admin only)
   * @param {string} id - Ticket ID
   * @param {string} assignedTo - Agent user ID
   * @returns {Promise}
   */
  assignTicket: async (id, assignedTo) => {
    try {
      const response = await api.put(`/admin/tickets/${id}/assign`, { assignedTo });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to assign ticket');
    }
  },

  /**
   * Update ticket status (admin only)
   * @param {string} id - Ticket ID
   * @param {string} status - New status
   * @returns {Promise}
   */
  updateTicketStatus: async (id, status) => {
    try {
      const response = await api.put(`/admin/tickets/${id}/status`, { status });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update ticket status');
    }
  },

  /**
   * Update ticket priority (admin only)
   * @param {string} id - Ticket ID
   * @param {string} priority - New priority
   * @returns {Promise}
   */
  updateTicketPriority: async (id, priority) => {
    try {
      const response = await api.put(`/admin/tickets/${id}/priority`, { priority });
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update ticket priority');
    }
  },

  /**
   * Add admin comment (admin only)
   * @param {string} id - Ticket ID
   * @param {Object} commentData - Comment data
   * @returns {Promise}
   */
  addAdminComment: async (id, commentData) => {
    try {
      const response = await api.post(`/admin/tickets/${id}/comments`, commentData);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add comment');
    }
  },

  /**
   * Get ticket analytics (admin only)
   * @param {string} id - Ticket ID
   * @returns {Promise}
   */
  getTicketAnalytics: async (id) => {
    try {
      const response = await api.get(`/admin/tickets/${id}/analytics`);
      return response.data.data || response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch ticket analytics');
    }
  },

  /**
   * Export tickets to CSV (admin only)
   * @param {Object} filters - Filter options
   * @returns {Promise}
   */
  exportTickets: async (filters = {}) => {
    try {
      const response = await api.post('/admin/tickets/export', filters, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export tickets');
    }
  }
};

